import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'cccisd-axios';
import notification from 'cccisd-notification';
import { Link } from 'cccisd-react-router';
import { useSelector } from 'react-redux';
import Loader from 'cccisd-loader';
import Tooltip from 'cccisd-tooltip';
import CardActions from './CardActions.js';
import ReporterSessionsTable from './ReporterSessionsTable';
import ManageReporters from './ManageReporters';
import { getFormattedDate } from 'js/helpers/index.js';
import style from './style.css';

// Icons
import ArrowRight from 'cccisd-icons/arrow-right20';
import ArrowDown from 'cccisd-icons/arrow-down20';
import IconPlus from 'cccisd-icons/plus-circle2';
import IconCheckmark from 'cccisd-icons/checkmark-circle';
import IconQuill from 'cccisd-icons/quill3';
import IconStop from 'cccisd-icons/cancel';
import IconClipboard from 'cccisd-icons/clipboard2';

const Fortress = window.cccisd.fortress;

const Card = props => {
    const { plan, progressList, progressLoading, collapsed, setCollapsed, setForceReloadQueries, filters } = props;
    const [surveyProgress, setSurveyProgress] = useState(null);
    const pawnInfo = useSelector(state => state.app.share.pawnInfo);
    const planData = plan;

    useEffect(() => {
        if (progressList && progressList.length > 0) {
            const progressData = {};
            progressList.forEach(progress => {
                const key = `${progress.sessionId}_${progress.pawnId}`;
                if (!progressData[key]) {
                    progressData[key] = {};
                }
                // Convert surveyProgressStatus to the expected format
                if (progress.surveyProgressStatus) {
                    Object.keys(progress.surveyProgressStatus).forEach(surveyHandle => {
                        progressData[key][surveyHandle] = progress.surveyProgressStatus[surveyHandle];
                    });
                }
            });
            setSurveyProgress(progressData);
        } else {
            setSurveyProgress(null);
        }
    }, [progressList]);
    const studentName = plan.selectedByPawn
        ? `${plan.selectedByPawn.fields.firstName} ${plan.selectedByPawn.fields.lastName}`
        : '';
    const isCollapsed = useMemo(() => collapsed.includes(plan.assignmentPlanId), [collapsed, plan.assignmentPlanId]);
    const isCompleted = useMemo(() => !!plan.completedDate, [plan.completedDate]);
    const stoppedDate = useMemo(() => {
        const planSettings = JSON.parse(plan.settings);
        return planSettings.stopAssessing;
    }, [plan.settings]);

    const planAssessmentProgress = useMemo(() => {
        if (!surveyProgress) return [];
        const assessmentProgress = [];

        plan.sessionList.forEach(session => {
            Object.values(session.sessionDeploymentInfo).forEach(pawn => {
                if (pawn.assigned === false) {
                    return;
                }
                session.settings.sessionCollection.forEach(survey => {
                    const thisSurveyInfo = JSON.parse(survey.resource.published_metadata).surveyInfo;
                    const surveyHandle = thisSurveyInfo.surveyHandle;
                    const surveyLabel = thisSurveyInfo.surveyLabel;
                    const pawnProgress = surveyProgress[`${session.sessionId}_${pawn.pawnId}`];
                    const pawnDetails = pawnInfo[pawn.pawnId];
                    let pawnSurveyProgress = null;
                    if (pawnProgress) {
                        if (pawnProgress[surveyHandle] === 2) {
                            pawnSurveyProgress = pawnProgress[surveyHandle];
                        }
                    }

                    assessmentProgress.push({
                        pawnDetails,
                        surveyHandle,
                        surveyLabel,
                        pawnSurveyProgress,
                    });
                });
            });
        });

        return assessmentProgress;
    }, [plan.sessionList, surveyProgress]);

    const thereAreCompletedSurveys = useMemo(() => {
        return planAssessmentProgress && planAssessmentProgress.some(p => p.pawnSurveyProgress === 2);
    }, [planAssessmentProgress]);

    const assessmentsInfo = useMemo(() => {
        const assessments = [];
        plan.sessionList
            .filter(session => !session.archivedAt)
            .forEach(session => {
                session.settings.sessionCollection.forEach(survey => {
                    const surveyInfo = JSON.parse(survey.resource.published_metadata).surveyInfo;
                    assessments.push({
                        label: surveyInfo.surveyLabel,
                        handle: surveyInfo.surveyHandle,
                    });
                });
            });
        return assessments;
    }, [plan.sessionList]);

    const handleToggleCollapse = () => {
        if (isCollapsed) {
            setCollapsed(prev => prev.filter(item => item !== plan.assignmentPlanId));
        } else {
            setCollapsed(prev => [...prev, plan.assignmentPlanId]);
        }
    };

    const handleAssignAgain = () => {
        const onSubmit = async vals => {
            let planSettings = JSON.parse(plan.settings);
            planSettings.stopAssessing = null;
            const res = await axios.post(window.cccisd.boilerplate.route('api.assignment.plan.create'), {
                creatingPawnId: Fortress.user.acting.id,
                label: plan.label + ' (Copy)',
                description: plan.description,
                pawnIds: [plan.selectedByPawn.pawn.pawnId],
                settings: planSettings,
                sessions: plan.sessionList.map((session, i) => {
                    return {
                        label: session.label,
                        deploymentId: session.deploymentId,
                        sessionType: session.sessionType,
                        offset: session.offset,
                        opensAt: new Date(),
                        closesAt: null,
                        settings: session.settings,
                        sessionDeploymentInfo: null, // Plan Controller will set this
                    };
                }),
            });
            if (res.status !== 200) {
                console.error('Error assigning again:', res);
                return;
            }
            setForceReloadQueries();
            notification({
                message: 'Assessments assigned successfully.',
            });
        };
        onSubmit();
    };

    const renderPlanStatus = () => {
        if (stoppedDate) {
            if (thereAreCompletedSurveys) {
                return (
                    <>
                        <span className="text-warning">
                            <IconCheckmark spaceRight />
                        </span>
                        Partially completed {getFormattedDate(stoppedDate)}
                    </>
                );
            }
            return (
                <>
                    <span className="text-danger">
                        <IconStop spaceRight />
                    </span>
                    Stopped {getFormattedDate(stoppedDate)}
                </>
            );
        }
        if (isCompleted) {
            return (
                <>
                    <span className="text-success">
                        <IconCheckmark spaceRight />
                    </span>
                    Completed {plan.completedDate}
                </>
            );
        }

        return (
            <span>
                <IconQuill /> Assigned {plan.createdDate}
            </span>
        );
    };

    const renderManageReporters = () => {
        const planStopped = !!JSON.parse(plan.settings)?.stopAssessing;
        const planCompleted = !!plan.completedDate;
        const planArchived = !!plan.archivedDate;
        const onlyStudentReportersInPlan = plan.sessionList.every(session => session.settings.reporter === 'Student');

        if (!planStopped && !planCompleted && !planArchived && !onlyStudentReportersInPlan) {
            return (
                <ManageReporters
                    setForceReloadQueries={setForceReloadQueries}
                    plan={plan}
                    studentName={studentName}
                    surveyProgress={surveyProgress}
                    thereAreCompletedSurveys={thereAreCompletedSurveys}
                />
            );
        }
    };

    return (
        <div className={`${style.assessmentCard} ${isCompleted && style.completed} ${stoppedDate && style.stopped}`}>
            <div className={style.cardContent}>
                <div className={style.cardHeader}>
                    <div className={style.col}>
                        <button
                            type="button"
                            className={'btn btn-secondary ' + style.unstyledBtn}
                            onClick={handleToggleCollapse}
                            aria-expanded={!isCollapsed}
                            aria-controls={`cardBody-${plan.assignmentPlanId}`}
                        >
                            {isCollapsed ? <ArrowRight /> : <ArrowDown />}
                        </button>
                    </div>
                    <div className={style.col}>
                        <h4>{studentName}</h4>
                    </div>
                    <div className={style.col}>
                        <span>{renderPlanStatus()}</span>
                    </div>
                    <div className={style.col}>
                        {!plan.archivedDate && (
                            <Tooltip title={`Reassign these assessments to ${studentName}.`}>
                                <button className="btn btn-primary" type="button" onClick={handleAssignAgain}>
                                    <IconPlus /> Assign Again
                                </button>
                            </Tooltip>
                        )}
                    </div>
                    <div className={style.col}>
                        {progressLoading && <Loader loading type="inline" />}
                        {thereAreCompletedSurveys && (
                            <Tooltip title="View assessment results.">
                                <Link
                                    to={`/results/${plan.selectedByPawn.pawn.pawnId}`}
                                    className="btn"
                                    style={{
                                        backgroundColor: '#2c817c',
                                        color: '#fff',
                                        fontSize: '1em',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px ',
                                        height: 'min-content',
                                    }}
                                >
                                    <IconClipboard spaceRight />
                                    <span>Results</span>
                                </Link>
                            </Tooltip>
                        )}
                    </div>
                    <div className={style.col}>
                        <p>
                            {isCollapsed &&
                                (assessmentsInfo.length > 1
                                    ? `${assessmentsInfo.length} Assessments`
                                    : `${assessmentsInfo[0]?.label || ''}`)}
                        </p>
                    </div>
                    <div className={style.col}>
                        <CardActions
                            plan={plan}
                            studentName={studentName}
                            setForceReloadQueries={setForceReloadQueries}
                            surveyProgress={surveyProgress}
                            isCompleted={isCompleted}
                            stoppedDate={stoppedDate}
                            planAssessmentProgress={planAssessmentProgress}
                            thereAreCompletedSurveys={thereAreCompletedSurveys}
                        />
                    </div>
                </div>
                <div
                    id={`cardBody-${plan.assignmentPlanId}`}
                    className={`${style.cardBody} ${!isCollapsed ? style.cardBodyExpanded : ''}`}
                    aria-label="Assessment Details"
                    aria-hidden={isCollapsed}
                >
                    <div className="ms-5 fw-light" style={{ fontSize: 'smaller' }}>
                        <div>
                            <span style={{ fontWeight: 'bold' }}>School: </span>
                            <span>{plan.selectedByPawn?.parentGroup?.site?.group?.label}</span>
                        </div>
                        {plan.selectedByPawn.fields.studentId && (
                            <div>
                                <span style={{ fontWeight: 'bold' }}>Student ID: </span>
                                <span>{plan.selectedByPawn?.fields?.studentId}</span>
                            </div>
                        )}
                    </div>
                    {progressLoading ? (
                        <Loader loading type="inline" />
                    ) : (
                        <ReporterSessionsTable
                            plan={plan}
                            setForceReloadQueries={setForceReloadQueries}
                            surveyProgress={surveyProgress}
                            setSurveyProgress={setSurveyProgress}
                            isCompleted={isCompleted}
                            stoppedDate={stoppedDate}
                            filters={filters}
                            planData={planData}
                            progress={progressList || []}
                        />
                    )}
                    {renderManageReporters()}
                </div>
            </div>
        </div>
    );
};

export default Card;

Card.propTypes = {
    plan: PropTypes.object.isRequired,
    progressList: PropTypes.array,
    progressLoading: PropTypes.bool,
    collapsed: PropTypes.arrayOf(PropTypes.number).isRequired,
    setCollapsed: PropTypes.func.isRequired,
    setForceReloadQueries: PropTypes.func.isRequired,
    filters: PropTypes.shape({
        ready: PropTypes.bool,
        stopped: PropTypes.bool,
        completed: PropTypes.bool,
        assessments: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        archived: PropTypes.string,
    }),
};

Card.defaultProps = {};
